import { ArrowUpRightIcon, TruckIcon } from '@heroicons/react/24/outline';
import { GQLOrderFragment, GQLOrderShippingMethodType, GQLOrderStatusType, GQLPaymentStatusType } from '@theprintbar/graphql';
import {
	capitaliseAndSpace,
	cn,
	getOrderPayment,
	getOrderShipmentGroups,
	getTrackingUrl,
	orderIsPartiallyShipped,
	sortedOrderStatuses,
} from '@theprintbar/shared';
import { ReactNode } from 'react';
import { OutlineAnchorButton } from '../Button';
import { Pill } from '../Pill';

interface OrderStatusBarProps {
	order: GQLOrderFragment;
	canCancel?: boolean;
}

export const getOrderStatusName = (order: GQLOrderFragment) => {
	if (order.status === GQLOrderStatusType.Shipped && order.shippingMethod === GQLOrderShippingMethodType.Pickup) {
		return 'Ready for pickup';
	}
	//Part of the order is on its way, the rest is still being made
	if (orderIsPartiallyShipped(order)) return 'Partially shipped';
	return capitaliseAndSpace(order.status);
};

export const getOrderStatusColour = (status: GQLOrderStatusType) => {
	switch (status) {
		case GQLOrderStatusType.PendingPayment:
		case GQLOrderStatusType.PendingApproval:
			return 'bg-orange';
		case GQLOrderStatusType.Cancelled:
			return 'bg-red';
		case GQLOrderStatusType.Delivered:
			return 'bg-green';
		default:
			return 'bg-blue';
	}
};

export function OrderStatusBar({ order }: OrderStatusBarProps) {
	const step = sortedOrderStatuses.indexOf(order.status);
	const pendingIndex = sortedOrderStatuses.indexOf(GQLOrderStatusType.PendingPayment);
	const processingIndex = sortedOrderStatuses.indexOf(GQLOrderStatusType.Picked);
	const shippingIndex = sortedOrderStatuses.indexOf(GQLOrderStatusType.Shipped);
	const deliveredIndex = sortedOrderStatuses.indexOf(GQLOrderStatusType.Delivered);
	const isPickup = order.shippingMethod === GQLOrderShippingMethodType.Pickup;

	const partiallyShipped = orderIsPartiallyShipped(order);
	const shipmentGroups = getOrderShipmentGroups(order.items, order.tracking);

	//A partially shipped order has reached the shipping step without completing it
	const equivalentStep = partiallyShipped
		? 3
		: step < pendingIndex
		? 0
		: step < processingIndex
		? 1
		: step < shippingIndex
		? 2
		: step < deliveredIndex
		? 3
		: 4;

	const cancelled = order.status === GQLOrderStatusType.Cancelled;
	const delivered = order.status === GQLOrderStatusType.Delivered;
	const shipped = order.status === GQLOrderStatusType.Shipped;
	const paymentFailed = getOrderPayment(order)?.status === GQLPaymentStatusType.Failed;

	const isShipped = order.shippingMethod !== GQLOrderShippingMethodType.Pickup && shipped;

	function Step({ number, children, className }: { number: number; children: ReactNode; className?: string }) {
		return (
			<div
				className={cn(
					'text-center',
					equivalentStep !== number ? 'text-grey' : 'text-blue',
					equivalentStep > number && 'opacity-60',
					className,
				)}
			>
				{children}
			</div>
		);
	}

	return (
		<div className="bg-white rounded-3xl border border-blue p-4 space-y-4">
			<div className="flex justify-between items-center">
				<h5 className="uppercase">Status</h5>
				<div className="flex items-center gap-2">
					{shipmentGroups.map((group, index) =>
						group.tracking ? (
							<a key={index} href={getTrackingUrl(group.tracking!.trackingNumber)} target="_blank" rel="noreferrer">
								<Pill className="bg-blue text-white py-0.5 px-2 underline">
									<TruckIcon className="w-4 h-4 mr-1" />
									{shipmentGroups.length <= 1
										? `Tracking #${group.tracking!.trackingNumber}`
										: `Shipment #${index + 1} tracking`}
								</Pill>
							</a>
						) : null,
					)}
				</div>
			</div>
			<div className="rounded-full bg-grey/10">
				<div
					className={cn('h-2 rounded-full', cancelled ? 'bg-red' : delivered || isShipped ? 'bg-green' : 'bg-blue')}
					style={{
						width: !equivalentStep
							? '5%'
							: cancelled || delivered || isShipped
							? '100%'
							: order.shippingMethod === GQLOrderShippingMethodType.Pickup
							? `calc((${equivalentStep} * 2 + 1) / 10 * 100%)`
							: `calc((${equivalentStep} * 2  + 1) / 8 * 100%)`,
					}}
				/>
			</div>
			<div className={cn('grid font-bold font-belfast text-xs text-grey/80', isPickup || cancelled ? 'grid-cols-5' : 'grid-cols-4')}>
				<Step
					number={0}
					className={cn('text-left', order.status === GQLOrderStatusType.PendingPayment && paymentFailed && 'text-red')}
				>
					{order.status === GQLOrderStatusType.PendingPayment ? (paymentFailed ? 'Payment Failed' : 'Unpaid') : 'Paid'}
				</Step>
				<Step number={1}>Order placed</Step>
				<Step number={2}>Processing</Step>
				<Step
					number={3}
					className={cn({
						'text-right': order.shippingMethod !== GQLOrderShippingMethodType.Pickup,
						'text-green': isShipped,
					})}
				>
					{order.shippingMethod === GQLOrderShippingMethodType.Pickup
						? 'Ready for pickup'
						: partiallyShipped
						? 'Partially shipped'
						: 'Shipped'}
				</Step>
				{(order.shippingMethod === GQLOrderShippingMethodType.Pickup || cancelled) && (
					<Step number={4} className={cn('text-right', cancelled ? 'text-red' : delivered ? 'text-green' : '')}>
						{cancelled ? 'Cancelled' : 'Picked up'}
					</Step>
				)}
			</div>
		</div>
	);
}
